import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updateData } from '../../../services';
import Modal from '../../helpers/Modal';
import {
    COUNTRIES,
    applyCountryChange,
    getPostalCodeLabel,
    getRegionSelectOptions,
    getStateProvinceLabel,
    normalizeRegionCode,
    resolveCountryCode,
} from '../../../context/usStates';
export const EditProjectForm = ({ project, onProjectUpdated }) => {
    const { token, base_url, setProjects, user } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const isSuperAdmin = user?.is_super_admin === true;

    const [formData, setFormData] = useState({
        project_name: '',
        project_type: '',
        description: '',
        shipping_details: '',
        site_address_1: '',
        site_address_2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'US'
    });

    // Initialize form data with project values when component mounts or project changes
    useEffect(() => {
        if (project) {
            setFormData({
                project_name: project.project_name || '',
                project_type: project.project_type || '',
                description: project.description || '',
                shipping_details: project.shipping_address?.details || '',
                site_address_1: project.site_address_1 || '',
                site_address_2: project.site_address_2 || '',
                city: project.city || '',
                state: normalizeRegionCode(project.state),
                zip_code: project.zip_code || '',
                country: resolveCountryCode(project.country, project.state)
            });
        }
    }, [project]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return; // Prevent multiple submissions

        if (isSuperAdmin && !formData.project_name.trim()) {
            window.showAlert(400, 'Project name is required.');
            return;
        }
        
        setIsSubmitting(true);

        const data = {
            ...(isSuperAdmin ? {
                project_name: formData.project_name.trim(),
                shipping_address: {
                    ...(project.shipping_address && typeof project.shipping_address === 'object'
                        ? project.shipping_address
                        : {}),
                    details: formData.shipping_details.trim()
                }
            } : {}),
            project_type: formData.project_type,
            description: formData.description,
            site_address_1: formData.site_address_1,
            site_address_2: formData.site_address_2,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zip_code,
            country: formData.country
        };

        try {
            console.log('Updating project:', project.id);
            const updated_project = await updateData(token, `${base_url}/apis/projects/${project.id}`, data);

            if (updated_project.project) {
                // Update the projects list in context
                setProjects(prevProjects => 
                    prevProjects.map(p => 
                        p.id === project.id ? updated_project.project : p
                    )
                );

                // Call the callback if provided
                if (onProjectUpdated) {
                    onProjectUpdated(updated_project.project);
                }

                // Show success alert
                window.showAlert(200, 'Project updated successfully!');
                console.log('Project updated successfully!');
                // Navigate back or to project page
                navigate(`/project/${project.id}`);
                
                // Close modal on success
                setIsModalOpen(false);
            }
        } catch (error) {
            console.error('Error updating project:', error);
            
            // Show error alert based on error type
            if (error.status === 404) {
                window.showAlert(404, 'Project not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid project data. Please check your input.');
            } else {
                window.showAlert(500, 'Failed to update project. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => (
            name === 'country' ? applyCountryChange(prev, value) : { ...prev, [name]: value }
        ));
    };

    if (!project) {
        return <div>No project data available</div>;
    }

    const siteCountry = resolveCountryCode(formData.country, formData.state);
    const siteRegions = getRegionSelectOptions(siteCountry, formData.state);
    const siteStateLabel = getStateProvinceLabel(siteCountry);
    const sitePostalLabel = getPostalCodeLabel(siteCountry);

    const editProjectForm = (
        <div>
            <h2 className="modal-title modal-title--compact">Edit Project Details</h2>
            <form onSubmit={handleSubmit} className="company-form-grid">
                {isSuperAdmin && (
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="project_name" className="form-label">
                                Project Name
                            </label>
                            <input
                                type="text"
                                id="project_name"
                                name="project_name"
                                value={formData.project_name}
                                onChange={handleChange}
                                className="form-input"
                                placeholder="Enter project name"
                                required
                            />
                        </div>
                    </div>
                )}

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="project_type" className="form-label">
                            Project Type
                        </label>
                        <select
                            id="project_type"
                            name="project_type"
                            value={formData.project_type}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="Commercial">Commercial</option>
                            <option value="Residential">Residential</option>
                            <option value="Agricultural">Agricultural</option>
                            <option value="Industrial">Industrial</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="description" className="form-label">
                            Project Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            className="form-textarea"
                            placeholder="Describe your project..."
                        />
                    </div>
                    {isSuperAdmin && (
                        <div className="form-group">
                            <label htmlFor="shipping_details" className="form-label">
                                Shipping Details
                            </label>
                            <textarea
                                id="shipping_details"
                                name="shipping_details"
                                value={formData.shipping_details}
                                onChange={handleChange}
                                rows="3"
                                className="form-textarea"
                                placeholder="Delivery instructions, dock hours, access notes..."
                            />
                        </div>
                    )}
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="site_address_1" className="form-label">
                            Site Address 1
                        </label>
                        <input
                            type="text"
                            id="site_address_1"
                            name="site_address_1"
                            value={formData.site_address_1}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter primary address"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="site_address_2" className="form-label">
                            Site Address 2
                        </label>
                        <input
                            type="text"
                            id="site_address_2"
                            name="site_address_2"
                            value={formData.site_address_2}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter secondary address (optional)"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="country" className="form-label">
                            Country
                        </label>
                        <select
                            id="country"
                            name="country"
                            value={siteCountry}
                            onChange={handleChange}
                            className="form-select"
                        >
                            {COUNTRIES.map((country) => (
                                <option key={country.code} value={country.code}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="city" className="form-label">
                            City
                        </label>
                        <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter city"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="state" className="form-label">
                            {siteStateLabel}
                        </label>
                        <select
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className="form-select"
                        >
                            <option value="">Select a {siteStateLabel.toLowerCase()}</option>
                            {siteRegions.map((region) => (
                                <option key={region.abbr} value={region.abbr}>
                                    {region.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="zip_code" className="form-label">
                            {sitePostalLabel}
                        </label>
                        <input
                            type="text"
                            id="zip_code"
                            name="zip_code"
                            value={formData.zip_code}
                            onChange={handleChange}
                            className="form-input"
                            placeholder={siteCountry === 'CA' ? 'A1A 1A1' : 'Enter zip code'}
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                            </>
                        ) : (
                            'Update Project'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div>
            <button className="edit-button" onClick={() => setIsModalOpen(true)}>
                <FontAwesomeIcon icon={faPen} />
            </button>
            {isModalOpen && (
                <Modal form={editProjectForm} setIsModalOpen={setIsModalOpen} isWide fitContent />
            )}
        </div>
    )
};
